import type { RatingsType } from '@shared/react-ui-core';
import {
  AsSeenOn,
  CoveEquipment,
  CustomerRatings,
  CustomersLoveCove,
  Divider,
  FAQ,
  FasterResponse,
  HomepageHero2,
  LearnMore,
  Reels,
  Showcase,
  SimpleSteps,
  USPIcons,
  useWindowSize,
} from '@shared/react-ui-core';
import { ImageType } from '@shared/react-ui-core/stories/Components/CoveEquipment';
import CoveNavLogo from '@website/components/Layout/coveNavLogo';
import Layout from '@website/components/Layout/layout';
import NavCta from '@website/components/Layout/navCta';
import AffirmPageBanner from '@website/components/affirm/AffirmPageBanner/AffirmPageBanner';
import EufyPartners from '@website/components/eufyPartners';
import Comparison from '@website/components/homeNew/experienceCove';
import Seo, { type SeoFragmentPage } from '@website/components/seo';
import { useMixpanel } from 'gatsby-plugin-mixpanel';
import { getCookie, setCookie } from '@website/libs/utils';
import { graphql } from 'gatsby';
import { useEffect, type FC } from 'react';
import useEmotive from '@website/hooks/useEmotive';

const defaultImages: ImageType[] = [
  {
    name: 'hero-section-presale',
    id: 'hero-image',
    image: {
      url: 'https://www.datocms-assets.com/10154/1696969721-call-230622-cove-0052_screen-1.png',
      alt: 'Cove equipment on table',
    },
  },
  {
    name: 'showcase-section',
    id: 'showcase-image',
    image: {
      url: 'https://www.datocms-assets.com/10154/1725890764-alarm-panel-and-vase-on-table.png',
      alt: 'alarm panel and vase on table',
    },
  },
];

const ProtectionPage: FC<{
  data: SeoFragmentPage & { datoCmsSiteMetaInfo: RatingsType } & {
    datoCmsComponent: { images: ImageType[] };
  };
  location: { pathname: string };
}> = ({ data, location }) => {
  const { edgetag } = useEmotive();
  const mixpanel = useMixpanel();
  const { width = 1920 } = useWindowSize();
  const { datoCmsSeo, datoCmsComponent } = data;
  const {
    images,
    reviews,
    heroImage,
    heroSubtext,
    heroText,
    hideBanner,
    navLogo,
  } = datoCmsSeo;
  const ratings = data.datoCmsSiteMetaInfo;

  const getImageByName = (name: string) => {
    const img = images?.filter((image) => image.name === name)?.[0]?.image;

    if (!img) {
      return defaultImages.filter((image) => image.name === name)?.[0]?.image;
    }

    return img;
  };

  const variant = 'lifestyle';

  useEffect(() => {
    const landingCookie = getCookie('landing');
    if (!landingCookie) {
      // eslint-disable-next-line no-restricted-globals
      setCookie('landing', location?.pathname, 90);
    }
    edgetag.trackViewContent({});
    mixpanel.register_once({ landing_page: location?.pathname });
  }, []);

  const title = heroText?.length
    ? heroText
    : 'New Technology. Better Protection.';
  const body = heroSubtext?.length
    ? heroSubtext
    : 'Our most advanced home security system yet.';

  const showcaseTitle = 'Home security that’s both effective and affordable.';
  const showcaseItems = [
    {
      text: 'Easy to set up and use (professional installation available)',
      imgURL:
        'https://www.datocms-assets.com/10154/1716310525-home_checkmark_icon.svg',
    },
    {
      text: 'Ethernet connection with cellular backup so you’re always connected',
      imgURL:
        'https://www.datocms-assets.com/10154/1716310966-lock_checkmark.svg',
    },
    {
      text: 'Faster emergency dispatch and fewer false alarms',
      imgURL:
        'https://www.datocms-assets.com/10154/1716311359-shield_checkmark_icon.svg',
    },
  ];

  return (
    <Layout
      isMinimal
      hidePromo={!!hideBanner}
      logoLink={<CoveNavLogo partnerImage={navLogo} />}
      navRight={width > 1150 ? undefined : <NavCta />}
    >
      <div>
        <span id="popout-quiz-trigger" style={{ width: '100%' }} />
        <div className="klaviyo-form-WSRfNr"></div>
        <div>
          <HomepageHero2
            image={heroImage || getImageByName('hero-section-pre-sale')}
            mobileImage={heroImage || getImageByName('hero-section-pre-sale')}
            variant={variant}
            title={title}
            subtitle={body}
          />
          <USPIcons />
          <AsSeenOn />
          <AffirmPageBanner />
          <Showcase
            image={getImageByName('showcase-section')}
            title={showcaseTitle}
            subtitle=""
            altItems={showcaseItems}
          />
          <CustomerRatings ratings={ratings} />
          <Divider className="mx-auto my-0 w-full max-w-xxl" />
          <EufyPartners />
          <CoveEquipment images={datoCmsComponent.images} />
          <SimpleSteps />
          <Reels />
          <FasterResponse />
          <Comparison title="It's an easy decision." />
          <CustomersLoveCove reviews={reviews} />
        </div>
        <FAQ />
        <LearnMore
          variant="sales"
          className="mb-cove-50 px-cove-25 py-cove-50 sm:px-cove-50 md:py-cove-50"
        />
      </div>
    </Layout>
  );
};

export default ProtectionPage;

export const Head = ({ data }: { data: SeoFragmentPage }) => (
  <Seo seo={data?.datoCmsSeo} />
);

export const query = graphql`
  query ($slug: String, $language: String) {
    ...SeoFragmentPage

    datoCmsSeo(path: { eq: $slug }, locale: $language) {
      heroText
      heroSubtext
      heroImage {
        alt
        url
      }
      navLogo {
        alt
        url
      }
      hideBanner
      reviews {
        profileImage {
          url
          alt
        }
        reviewContent
        reviewDate
        reviewerName
        customData
        id
      }
    }

    datoCmsComponent(name: { eq: "Cove Equipment" }) {
      images {
        id
        name
        image {
          alt
          url
        }
      }
    }
  }
`;
